/**
 * Alshallal Pastry Company HR System - Module Visibility Page
 * Phase 18.10: Module Visibility Controls
 * 
 * Access: Super Admin ONLY
 * 
 * Allows Super Admin to toggle module visibility per role.
 * Changes are saved to database and enforce both UI and API access.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Loader2,
  AlertTriangle,
  Shield,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role display mappings. Every role appears here, including the eight legacy
// HR sub-roles: this screen is the only place a Super Admin can switch modules
// back on for an account that still holds one, so hiding them would strand it.
const ROLE_DISPLAY = {
  employee: { en: 'Employee', ar: 'موظف' },
  manager: { en: 'Manager', ar: 'مدير' },
  hr_assistant: { en: 'HR Assistant', ar: 'مساعد الموارد البشرية' },
  hr_manager: { en: 'HR Manager', ar: 'مدير الموارد البشرية' },
  hr_admin: { en: 'HR Admin', ar: 'مسؤول الموارد البشرية' },
  it_inf: { en: 'IT INF', ar: 'تقنية المعلومات والبنية التحتية' },
  super_admin: { en: 'Super Admin', ar: 'المسؤول الأعلى' },
  hr_ops: { en: 'HR Operations (legacy)', ar: 'عمليات الموارد البشرية (سابق)' },
  hr_er: { en: 'Employee Relations (legacy)', ar: 'علاقات الموظفين (سابق)' },
  hr_gr: { en: 'Government Relations (legacy)', ar: 'العلاقات الحكومية (سابق)' },
  hr_ld: { en: 'Learning & Development (legacy)', ar: 'التعلم والتطوير (سابق)' },
  hr_pc: { en: 'Payroll & Compensation (legacy)', ar: 'الرواتب والمكافآت (سابق)' },
  hr_ta: { en: 'Talent Acquisition (legacy)', ar: 'استقطاب المواهب (سابق)' },
  hr_sys: { en: 'HR System Admin (legacy)', ar: 'مسؤول نظام الموارد البشرية (سابق)' },
  hr_aud: { en: 'HR Auditor (legacy)', ar: 'مدقق الموارد البشرية (سابق)' },
};

const ModuleVisibilityPage = () => {
  const { token, hasRole } = useAuth();
  const { isRTL } = useLanguage();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policies, setPolicies] = useState({});
  const [moduleMetadata, setModuleMetadata] = useState({});
  const [selectedRole, setSelectedRole] = useState('employee');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPolicy, setOriginalPolicy] = useState({});
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  // RBAC check - Super Admin ONLY
  const canAccess = hasRole('super_admin');
  
  // Fetch module policy
  const fetchPolicy = useCallback(async () => {
    if (!canAccess) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/module-policy/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail?.error || 'Failed to fetch module policy');
      }
      
      const data = await response.json();
      setPolicies(data.policies || {});
      setModuleMetadata(data.module_metadata || {});
      setOriginalPolicy(JSON.parse(JSON.stringify(data.policies || {})));
    } catch (error) {
      console.error('Error fetching policy:', error);
      toast.error(isRTL ? 'فشل في تحميل سياسة الوحدات' : 'Failed to load module policy');
    } finally {
      setLoading(false);
    }
  }, [token, canAccess, isRTL]);
  
  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);
  
  // Check for changes
  useEffect(() => {
    const currentPolicy = JSON.stringify(policies);
    const original = JSON.stringify(originalPolicy);
    setHasChanges(currentPolicy !== original);
  }, [policies, originalPolicy]);
  
  // Toggle module for selected role
  const toggleModule = (moduleKey, enabled) => {
    // A restricted module names the roles it may be enabled for (2026-06:
    // Access & Roles is HR + IT + owner, Module Visibility is IT + owner).
    const metadata = moduleMetadata[moduleKey];
    const audience = metadata?.audience;
    if (audience && selectedRole === 'super_admin' && !enabled) {
      toast.error(isRTL ? 'لا يمكن تعطيل هذه الوحدة للمسؤول الأعلى' : 'Cannot disable this module for Super Admin');
      return;
    }
    
    if (audience && !audience.includes(selectedRole) && enabled) {
      toast.error(
        (isRTL ? 'هذه الوحدة متاحة فقط لـ: ' : 'This module is only available to: ')
        + audience.join(', ')
      );
      return;
    }
    
    setPolicies(prev => ({
      ...prev,
      [selectedRole]: {
        ...(prev[selectedRole] || {}),
        [moduleKey]: enabled
      }
    }));
  };
  
  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const rolePolicy = policies[selectedRole] || {};
      
      const response = await fetch(`${API_URL}/api/admin/module-policy/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role: selectedRole,
          modules: rolePolicy
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail?.error || 'Failed to save module policy');
      }
      
      toast.success(isRTL ? 'تم حفظ التغييرات بنجاح' : 'Changes saved successfully');
      setOriginalPolicy(JSON.parse(JSON.stringify(policies)));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const handleReset = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/module-policy/reset-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail?.error || 'Failed to reset module policy');
      }
      
      toast.success(isRTL ? 'تم إعادة التعيين إلى الإعدادات الافتراضية' : 'Reset to defaults successfully');
      setResetDialogOpen(false);
      fetchPolicy();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Group modules by category
  const getGroupedModules = () => {
    const groups = {
      core: { label: { en: 'Core', ar: 'الأساسية' }, modules: [] },
      hr: { label: { en: 'HR Management', ar: 'إدارة الموارد البشرية' }, modules: [] },
      approvals: { label: { en: 'Approvals', ar: 'الموافقات' }, modules: [] },
      admin: { label: { en: 'Administration', ar: 'الإدارة' }, modules: [] },
      settings: { label: { en: 'Settings', ar: 'الإعدادات' }, modules: [] },
    };
    
    Object.entries(moduleMetadata).forEach(([key, meta]) => {
      const module = { key, ...meta };
      
      if (key.startsWith('settings_')) {
        groups.settings.modules.push(module);
      } else if (['admin_console', 'monitoring', 'system', 'audit'].includes(key)) {
        groups.admin.modules.push(module);
      } else if (key.includes('approvals') || key.includes('admin') && !key.startsWith('admin')) {
        groups.approvals.modules.push(module);
      } else if (['employees', 'documents', 'documents_verification', 'import', 'org_chart'].includes(key)) {
        groups.hr.modules.push(module);
      } else {
        groups.core.modules.push(module);
      }
    });
    
    return groups;
  };
  
  // Get current value for a module
  const getModuleValue = (moduleKey) => {
    const rolePolicy = policies[selectedRole] || {};
    return rolePolicy[moduleKey] ?? false;
  };
  
  // Check if module is locked for this role (restricted audience)
  const isModuleLocked = (moduleKey) => {
    const audience = moduleMetadata[moduleKey]?.audience;
    return audience ? !audience.includes(selectedRole) : false;
  };
  
  // Access denied view
  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {isRTL ? 'غير مصرح' : 'Access Denied'}
        </h2>
        <p className="text-slate-600">
          {isRTL 
            ? 'فقط المسؤول الأعلى يمكنه الوصول إلى إعدادات إظهار الوحدات'
            : 'Only Super Admin can access module visibility settings'}
        </p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  const groupedModules = getGroupedModules();
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-red-600" />
            <h1 className="text-2xl font-bold text-slate-900">
              {isRTL ? 'إظهار الوحدات' : 'Module Visibility'}
            </h1>
          </div>
          <p className="text-slate-600 text-sm mt-1">
            {isRTL 
              ? 'التحكم في الوحدات المتاحة لكل دور (المسؤول الأعلى فقط)'
              : 'Control which modules are available for each role (Super Admin only)'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setResetDialogOpen(true)}
            disabled={saving}
          >
            <RotateCcw className="h-4 w-4 me-2" />
            {isRTL ? 'إعادة التعيين' : 'Reset Defaults'}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 me-2" />
            )}
            {isRTL ? 'حفظ التغييرات' : 'Save Changes'}
          </Button>
        </div>
      </div>
      
      {/* Role Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{isRTL ? 'اختر الدور' : 'Select Role'}</CardTitle>
          <CardDescription>
            {isRTL 
              ? 'اختر الدور لتعديل إعدادات الوحدات الخاصة به'
              : 'Choose a role to configure its module visibility'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-full sm:w-[300px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_DISPLAY).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {isRTL ? value.ar : value.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
      
      {/* Module Groups */}
      {Object.entries(groupedModules).map(([groupKey, group]) => {
        if (group.modules.length === 0) return null;
        
        return (
          <Card key={groupKey}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {isRTL ? group.label.ar : group.label.en}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {group.modules.map((module) => {
                  const isEnabled = getModuleValue(module.key);
                  const isLocked = isModuleLocked(module.key);
                  
                  return (
                    <div 
                      key={module.key}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border",
                        isEnabled ? "bg-green-50/50 border-green-200" : "bg-slate-50/50 border-slate-200",
                        isLocked && "opacity-60"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {isEnabled ? (
                          <Eye className="h-5 w-5 text-green-600" />
                        ) : (
                          <EyeOff className="h-5 w-5 text-slate-400" />
                        )}
                        <div>
                          <div className="font-medium text-sm">
                            {isRTL ? module.label_ar : module.label_en}
                          </div>
                          <div className="text-xs text-slate-500">
                            {module.description_en}
                          </div>
                        </div>
                        {module.audience && (
                          <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">
                            <Lock className="h-3 w-3 me-1" />
                            {module.audience.join(', ')}
                          </Badge>
                        )}
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) => toggleModule(module.key, checked)}
                        disabled={isLocked || (module.audience && selectedRole === 'super_admin')}
                        data-testid={`module-toggle-${module.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {/* Reset Confirmation Dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isRTL ? 'إعادة التعيين إلى الإعدادات الافتراضية؟' : 'Reset to Defaults?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRTL 
                ? 'سيتم إعادة تعيين جميع إعدادات إظهار الوحدات لجميع الأدوار إلى الإعدادات الافتراضية. هذا الإجراء لا يمكن التراجع عنه.'
                : 'This will reset all module visibility settings for all roles to their default values. This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRTL ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} className="bg-red-600 hover:bg-red-700">
              {saving && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
              {isRTL ? 'إعادة التعيين' : 'Reset'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModuleVisibilityPage;
